import axios from "axios";
import { formatCurrency as formatCurrencyUtil, formatCompactCurrency as formatCompactCurrencyUtil } from '../utils/moneyUtils';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5001",
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent caching issues
    if (config.method === "get") {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common HTTP errors
    if (error.response) {
      const { status, data, config } = error.response;

      switch (status) {
        case 401:
          // Only auto-logout for authentication errors, not authorization errors
          // Skip auto-logout for specific endpoints that might have permission issues
          const skipAutoLogout =
            config.url?.includes('/api/jobs/') || // Job polling - might access old/deleted jobs
            data.message?.includes('permission') || // Permission denied (different from token invalid)
            data.message?.includes('not found'); // Resource not found errors

          if (!skipAutoLogout) {
            // Unauthorized - token expired or invalid
            console.warn("Authentication failed, logging out:", data.message);
            localStorage.removeItem("token");
            delete api.defaults.headers.common["Authorization"];
            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }
          } else {
            // Log but don't redirect for permission/access issues
            console.warn("Access denied:", data.message);
          }
          break;
        case 403:
          // Forbidden - insufficient permissions
          console.error("Access denied:", data.message);
          break;
        case 404:
          // Not found
          console.error("Resource not found:", data.message);
          break;
        case 500:
          // Server error
          console.error("Server error:", data.message);
          break;
        default:
          console.error("API Error:", data.message || "Unknown error");
      }
    } else if (error.request) {
      // Network error
      console.error("Network error:", error.message);
    }

    return Promise.reject(error);
  }
);

// API methods
export const authAPI = {
  login: (credentials) => api.post("/api/auth/login", credentials),
  register: (userData) => api.post("/api/auth/register", userData),
  getProfile: () => api.get("/api/auth/profile"),
  updateProfile: (profileData) => api.put("/api/auth/profile", profileData),
  changePassword: (passwordData) =>
    api.put("/api/auth/change-password", passwordData),
  logout: () => api.post("/api/auth/logout"),
  // OTP verification
  sendOTP: (email) => api.post("/api/auth/send-otp", { email }),
  verifyOTP: (email, otp) => api.post("/api/auth/verify-otp", { email, otp }),
  // Forgot password
  forgotPassword: (email) => api.post("/api/auth/forgot-password", { email }),
  verifyResetOtp: (sessionId, otp) => api.post("/api/auth/verify-reset-otp", { sessionId, otp }),
  resetPassword: (sessionId, newPassword) => api.post("/api/auth/reset-password", { sessionId, newPassword }),
};

export const bondsAPI = {
  // Get all bonds with filtering and pagination
  getBonds: (params = {}) => api.get("/api/bonds", { params }),

  // Get bonds for maturity management (current date before maturity date)
  getMaturityBonds: (params = {}) => api.get("/api/bonds/maturity", { params }),

  // Get single bond
  getBond: (id) => api.get(`/api/bonds/${id}`),
  getBondById: (id) => api.get(`/api/bonds/${id}`), // Alias for consistency

  // Get token holders for a bond
  getBondHolders: (id) => api.get(`/api/bonds/${id}/holders`),

  // Get bond holders (for trustees - uses bondId param)
  getBondHoldersByBondId: (bondId) => api.get(`/api/bonds/${bondId}/holders`),

  // Create new bond
  createBond: (bondData) => api.post("/api/bonds", bondData),

  // Update bond
  updateBond: (id, bondData) => api.put(`/api/bonds/${id}`, bondData),

  // Delete bond
  deleteBond: (id) => api.delete(`/api/bonds/${id}`),

  // Submit bond for approval
  submitBond: (id) => api.post(`/api/bonds/${id}/submit`),

  // Approve bond (Arranger/Exchange)
  approveBond: (id, data) => api.post(`/api/bonds/${id}/approve`, data),

  // Reject bond (Arranger/Exchange)
  rejectBond: (id, data) => api.post(`/api/bonds/${id}/reject`, data),

  // Internal issuer approvals
  internalApproveBond: (id, level, data = {}) =>
    api.post(`/api/bonds/${id}/internal/approve`, { level, ...data }),
  internalRejectBond: (id, data = {}) =>
    api.post(`/api/bonds/${id}/internal/reject`, data),

  // Update bond status (Facility Agent)
  updateBondStatus: (id, data) => api.put(`/api/bonds/${id}/status`, data),

  // Get dashboard statistics
  getDashboardStats: () => api.get("/api/bonds/dashboard/stats"),

  // Get bond documents
  getBondDocuments: (id, params = {}) => api.get(`/api/bonds/${id}/documents`, { params }),

  // Upload payment reference files for redemption
  uploadPaymentReference: (id, formData) =>
    api.post(`/api/bonds/${id}/payment-reference`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  // Get bonds for redemption management with pagination
  getRedemptionBondsConsolidated: (params = {}) =>
    api.get("/api/bonds/redemption/consolidated", { params }),

  // Redemption approval endpoints
  getBondRedemption: (redemptionId) =>
    api.get(`/api/bonds/redemption/${redemptionId}`),

  getBondRedemptionsByBond: (bondId) =>
    api.get(`/api/bonds/${bondId}/redemptions`),

  getPendingL1Redemptions: (params = {}) =>
    api.get("/api/bonds/redemption/pending/l1", { params }),

  getPendingL2Redemptions: (params = {}) =>
    api.get("/api/bonds/redemption/pending/l2", { params }),

  approveRedemptionL1: (redemptionId, data = {}) =>
    api.post(`/api/bonds/redemption/${redemptionId}/approve-l1`, data),

  rejectRedemptionL1: (redemptionId, data = {}) =>
    api.post(`/api/bonds/redemption/${redemptionId}/reject-l1`, data),

  approveRedemptionL2: (redemptionId, data = {}) =>
    api.post(`/api/bonds/redemption/${redemptionId}/approve-l2`, data),

  rejectRedemptionL2: (redemptionId, data = {}) =>
    api.post(`/api/bonds/redemption/${redemptionId}/reject-l2`, data),
};

export const bondTrusteesAPI = {
  // Get all trustee assignments
  getAssignments: (params = {}) => api.get("/api/bond-trustees", { params }),

  // Get trustees for a specific bond
  getBondTrustees: (bondId) => api.get(`/api/bond-trustees/bond/${bondId}`),

  // Get bonds for a specific trustee (or current user if trustee)
  getTrusteeBonds: (trusteeId) => api.get(`/api/bond-trustees/trustee/${trusteeId}`),

  // Assign trustee to bond (issuer/arranger/super_admin only)
  assignTrustee: (data) => api.post("/api/bond-trustees", data),

  // Update assignment (issuer/arranger/super_admin only)
  updateAssignment: (id, data) => api.patch(`/api/bond-trustees/${id}`, data),

  // Remove assignment (issuer/arranger/super_admin only)
  removeAssignment: (id) => api.delete(`/api/bond-trustees/${id}`),
};

export const fundingAPI = {
  // Get funding overview for a bond
  getFundingOverview: (bondId) => api.get(`/api/funding/bond/${bondId}`),

  // Deposit funds
  depositFunds: (data) => api.post("/api/funding/deposit", data),

  // Withdraw funds
  withdrawFunds: (data) => api.post("/api/funding/withdraw", data),

  // Fund specific payment
  fundPayment: (data) => api.post("/api/funding/fund-payment", data),

  // Execute payment
  executePayment: (data) => api.post("/api/funding/execute-payment", data),

  // Get funding history
  getFundingHistory: (bondId) => api.get(`/api/funding/history/${bondId}`),

  // Get upcoming payments (Facility Agent)
  getUpcomingPayments: (params = {}) =>
    api.get("/api/funding/upcoming-payments", { params }),

  // Confirm funding (Facility Agent)
  confirmFunding: (paymentId, data) =>
    api.post(`/api/funding/payments/${paymentId}/confirm`, data),

  // Generate RENTAS file (Facility Agent)
  generateRentasFile: (paymentId) =>
    api.post(`/api/funding/payments/${paymentId}/rentas`),

  // Upload payment proof (Facility Agent)
  uploadPaymentProof: (paymentId, formData) =>
    api.post(`/api/funding/payments/${paymentId}/proof`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

export const announcementsAPI = {
  // Get all announcements
  getAnnouncements: (params = {}) => api.get("/api/announcements", { params }),

  // Get single announcement
  getAnnouncement: (id) => api.get(`/api/announcements/${id}`),

  // Create announcement (supports FormData for attachments)
  createAnnouncement: (data) => {
    const config =
      typeof FormData !== "undefined" && data instanceof FormData
        ? { headers: { "Content-Type": undefined } }
        : {};
    return api.post("/api/announcements", data, config);
  },

  // Update announcement (supports FormData for attachments)
  updateAnnouncement: (id, data) => {
    const config =
      typeof FormData !== "undefined" && data instanceof FormData
        ? { headers: { "Content-Type": undefined } }
        : {};
    return api.put(`/api/announcements/${id}`, data, config);
  },

  // Delete announcement
  deleteAnnouncement: (id) => api.delete(`/api/announcements/${id}`),

  // Remove a single attachment from an announcement
  removeAttachment: (announcementId, fileId) =>
    api.delete(`/api/announcements/${announcementId}/attachments/${fileId}`),

  // Get recent announcements for dashboard
  getRecentAnnouncements: (limit = 5) =>
    api.get("/api/announcements/dashboard/recent", {
      params: { limit },
    }),

  // Get announcements for specific bond
  getBondAnnouncements: (bondId, params = {}) =>
    api.get(`/api/announcements/bond/${bondId}`, { params }),

  // Approval workflow
  submitForL1: (id) => api.post(`/api/announcements/${id}/submit-l1`),
  approveL1: (id) => api.post(`/api/announcements/${id}/approve-l1`),
  approveL2: (id) => api.post(`/api/announcements/${id}/approve-l2`),
};

// File services (metadata and PocketBase cleanup)
export const filesAPI = {
  // Upload file
  uploadFile: (formData) =>
    api.post("/api/files", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  // Get file metadata by backend file id
  getFile: (fileId) => api.get(`/api/files/${fileId}`),

  // Download file content
  downloadFile: (fileId) =>
    api.get(`/api/files/${fileId}/content`, { responseType: "blob" }),

  // List files
  listFiles: (params = {}) => api.get("/api/files", { params }),

  // Delete file by backend file id (if supported by backend)
  deleteFile: (fileId) => api.delete(`/api/files/${fileId}`),

  // Delete the underlying PocketBase record by pbRecordId (if supported by backend)
  deletePocketBaseRecord: (pbRecordId) =>
    api.delete(`/api/files/pbrecord/${pbRecordId}`),
};

export const reportsAPI = {
  // Get available report types
  getReportTypes: () => api.get("/api/reports/types"),

  // Generate bond performance report
  generateBondPerformanceReport: (data) =>
    api.post("/api/reports/bond-performance", data),

  // Generate payment history report
  generatePaymentHistoryReport: (data) =>
    api.post("/api/reports/payment-history", data),

  // Generate user activity report (Admin only)
  generateUserActivityReport: (data) =>
    api.post("/api/reports/user-activity", data),

  // Generate compliance report
  generateComplianceReport: (data) => api.post("/api/reports/compliance", data),
};

export const usersAPI = {
  // Get all users (Admin only)
  getUsers: (params = {}) => api.get("/api/users", { params }),

  // Create new user (Admin only)
  createUser: (userData) => {
    // If userData is FormData, remove Content-Type header to let browser set it
    const config =
      userData instanceof FormData
        ? {
          headers: {
            "Content-Type": undefined, // This allows browser to set multipart/form-data with boundary
          },
        }
        : {};

    return api.post("/api/users/create", userData, config);
  },

  // Get single user
  getUser: (id) => api.get(`/api/users/${id}`),

  // Update user
  updateUser: (id, data) => api.put(`/api/users/${id}`, data),

  // Update user status
  updateUserStatus: (id, data) => api.put(`/api/users/${id}/status`, data),

  // Approve user registration
  approveUser: (id) => api.put(`/api/users/${id}/approve`),

  // Reject user registration
  rejectUser: (id, reason) => api.put(`/api/users/${id}/reject`, { reason }),

  // Download user document
  downloadDocument: (userId, documentType) =>
    api.get(`/api/users/${userId}/documents/${documentType}/download`, {
      responseType: "blob",
    }),

  // Reset user password
  resetPassword: (id) => api.post(`/api/users/${id}/reset-password`),

  // Activate user
  activateUser: (id) => api.put(`/api/users/${id}/activate`),

  // Deactivate user
  deactivateUser: (id) => api.put(`/api/users/${id}/deactivate`),

  // Delete user
  deleteUser: (id) => api.delete(`/api/users/${id}`),

  // Get user statistics (Admin only)
  getUserStats: () => api.get("/api/users/dashboard/stats"),

  // Get system statistics (Super Admin only)
  getSystemStats: () => api.get("/api/users/system/stats"),

  // Get recent activities
  getRecentActivities: (params = {}) =>
    api.get("/api/users/activities", { params }),

  // Get custodian statistics
  getCustodianStats: () => api.get("/api/users/custodian/stats"),

  // Check email availability
  checkEmailAvailability: (email) =>
    api.get("/api/users/check-email", { params: { email } }),

  // Sub Account Management
  // Get sub accounts for current user
  getSubAccounts: (params = {}) =>
    api.get("/api/users/sub-accounts", { params }),

  // Create sub account
  createSubAccount: (subAccountData) =>
    api.post("/api/users/sub-accounts", subAccountData),

  // Create sub account with approval logic
  createSubAccountWithApproval: (subAccountData) =>
    api.post("/api/users/sub-accounts", subAccountData),

  // Get single sub account
  getSubAccount: (id) => api.get(`/api/users/sub-accounts/${id}`),

  // Update sub account
  updateSubAccount: (id, data) =>
    api.put(`/api/users/sub-accounts/${id}`, data),

  // Delete sub account
  deleteSubAccount: (id) => api.delete(`/api/users/sub-accounts/${id}`),

  // Activate sub account
  activateSubAccount: (id) => api.put(`/api/users/sub-accounts/${id}/activate`),

  // Deactivate sub account
  deactivateSubAccount: (id) =>
    api.put(`/api/users/sub-accounts/${id}/deactivate`),

  // Two-level approval system
  // Submit approval request for user actions
  submitApprovalRequest: (requestData) => {
    // If requestData is FormData, let axios handle Content-Type automatically
    if (typeof FormData !== "undefined" && requestData instanceof FormData) {
      return api.post("/api/users/approval-requests", requestData, {
        headers: { "Content-Type": undefined } // Let browser set Content-Type with boundary
      });
    }
    // Otherwise, send as JSON
    return api.post("/api/users/approval-requests", requestData);
  },

  // Get pending approval requests
  getPendingApprovalRequests: (params = {}) =>
    api.get("/api/users/approval-requests", { params }),

  // Get approval request by ID
  getApprovalRequest: (id) => api.get(`/api/users/approval-requests/${id}`),

  // Approve L2 request
  approveL2Request: (id, data) =>
    api.put(`/api/users/approval-requests/${id}/approve`, data),

  // Reject L2 request
  rejectL2Request: (id, data) =>
    api.put(`/api/users/approval-requests/${id}/reject`, data),

  // Sub-account approval methods
  getPendingSubAccountApprovals: (params = {}) =>
    api.get("/api/users/sub-accounts/approval-requests/pending", { params }),
  getSubAccountApprovalHistory: (params = {}) =>
    api.get("/api/users/sub-accounts/approval-requests/history", { params }),

  // Approve sub-account creation request (using general approval endpoint)
  approveSubAccountRequest: (requestId, approvalData = {}) =>
    api.put(`/api/users/approval-requests/${requestId}/approve`, approvalData),

  // Reject sub-account creation request (using general approval endpoint)
  rejectSubAccountRequest: (requestId, rejectionData = {}) =>
    api.put(`/api/users/approval-requests/${requestId}/reject`, rejectionData),

  // Get approval history
  getApprovalHistory: (params = {}) =>
    api.get("/api/users/approval-history", { params }),
};

export const systemSettingsAPI = {
  // Get system settings (Super Admin only)
  getSettings: () => api.get("/api/system-settings"),

  // Update system settings (Super Admin only)
  updateSettings: (settingsData) =>
    api.put("/api/system-settings", settingsData),

  // Get public settings (no authentication required - for login page)
  getPublicSettings: () => api.get("/api/system-settings/public"),
};

export const ipWhitelistAPI = {
  // Get all IP whitelist entries (Super Admin only)
  getIPWhitelist: () => api.get("/api/ip-whitelist"),

  // Add new IP to whitelist (Super Admin only)
  addIP: (ipData) => api.post("/api/ip-whitelist", ipData),

  // Update IP whitelist entry (Super Admin only)
  updateIP: (id, ipData) => api.put(`/api/ip-whitelist/${id}`, ipData),

  // Delete IP from whitelist (Super Admin only)
  deleteIP: (id) => api.delete(`/api/ip-whitelist/${id}`),

  // Toggle IP whitelist status (Super Admin only)
  toggleIP: (id) => api.patch(`/api/ip-whitelist/${id}/toggle`),
};

export const investorRolesAPI = {
  // Get all roles for the investor
  getRoles: (params = {}) => api.get("/api/investor-roles", { params }),

  // Get role statistics
  getRoleStats: () => api.get("/api/investor-roles/stats"),

  // Get available permissions
  getAvailablePermissions: () => api.get("/api/investor-roles/permissions"),

  // Get single role
  getRole: (roleId) => api.get(`/api/investor-roles/${roleId}`),

  // Create new role
  createRole: (roleData) => api.post("/api/investor-roles", roleData),

  // Update role
  updateRole: (roleId, roleData) =>
    api.put(`/api/investor-roles/${roleId}`, roleData),

  // Delete role
  deleteRole: (roleId) => api.delete(`/api/investor-roles/${roleId}`),

  // Assign role to sub-account
  assignRoleToSubAccount: (roleId, subAccountId) =>
    api.post("/api/investor-roles/assign", { roleId, subAccountId }),

  // Remove role from sub-account
  removeRoleFromSubAccount: (subAccountId) =>
    api.delete(`/api/investor-roles/assign/${subAccountId}`),

  // Get pending role approval requests
  getPendingRoleApprovals: () =>
    api.get("/api/investor-roles/pending-approvals"),

  // Get role approval history
  getRoleApprovalHistory: () => api.get("/api/investor-roles/approval-history"),

  // Approve role creation request (using general approval API)
  approveRole: async (requestId) => {
    const response = await api.put(
      `/api/users/approval-requests/${requestId}/approve`
    );
    return response.data;
  },

  // Reject role creation request (using general approval API)
  rejectRole: async (requestId, reason) => {
    const response = await api.put(
      `/api/users/approval-requests/${requestId}/reject`,
      { reason }
    );
    return response.data;
  },
};

export const walletAPI = {
  // Get all wallets (Super Admin only)
  getWallets: (params = {}) => api.get("/api/wallets", { params }),

  // Get wallet details
  getWallet: (walletId) => api.get(`/api/wallets/${walletId}`),

  // Get wallet by user ID
  getWalletByUserId: (userId) => api.get(`/api/wallets/user/${userId}`),
  // Get all wallets for a user (conventional and Islamic)
  getWalletsByUser: async (userId) => {
    try {
      const res = await api.get(`/api/wallets/user/${userId}/all`);
      return res;
    } catch (e) {
      if (e?.response?.status === 404) {
        return { data: { wallets: [] } };
      }
      throw e;
    }
  },

  // Get current user's wallet
  getMyWallet: () => api.get("/api/wallets/my-wallet"),
  // Get current user's wallets (all types)
  getMyWallets: () => api.get("/api/wallets/my-wallets"),

  // Get users without wallets
  getUsersWithoutWallets: () => api.get("/api/wallets/users-without-wallets"),

  // Create wallet
  createWallet: (walletData) => api.post("/api/wallets/create", walletData),

  // Update wallet
  updateWallet: (walletId, walletData) =>
    api.put(`/api/wallets/${walletId}`, walletData),

  // Get wallet statistics
  getWalletStats: () => api.get("/api/wallets/stats/overview"),
};

export const investorAPI = {
  // Get available counterparties for investors (direct investors and custodians, excluding sub-investors)
  getCounterparties: (params = {}) =>
    api.get("/api/investor/counterparties", { params }),
  // Get a counterparty's wallet (investor-safe, minimal data)
  getCounterpartyWallet: (userId) =>
    api.get(`/api/investor/counterparties/${userId}/wallet`),
};

export const holdingsAPI = {
  // Get all holdings (admin/PS overview)
  getAllHoldings: (params = {}) => api.get('/api/holdings', { params }),

  // Get my holdings (as investor)
  getMyHoldings: (params = {}) => api.get('/api/holdings/my', { params }),

  // Get holdings as holder (custodian holding on behalf of others)
  getHoldingsAsHolder: (params = {}) => api.get('/api/holdings/as-holder', { params }),

  // Get holdings created by custodian (allocations to sub-investors)
  getHoldingsAsCustodian: (params = {}) => api.get('/api/holdings/as-custodian', { params }),

  // Get holdings by bond (admin only)
  getHoldingsByBond: (bondId, params = {}) => api.get(`/api/holdings/bond/${bondId}`, { params }),

  // Get bonds with holdings summary
  getBondsWithHoldings: (params = {}) => api.get('/api/holdings/bonds-with-holdings', { params }),

  // Get allocation report for a specific bond
  getAllocationReport: (bondId) => api.get(`/api/holdings/allocation-report/${bondId}`),
};

export const custodianAPI = {
  // Get all sub-investors for current custodian
  getSubInvestors: (params = {}) =>
    api.get("/api/custodian/sub-investors", { params }),

  // Get custodian's wallet information
  getCustodianWallets: () => api.get("/api/custodian/wallets"),

  // Get custodian's own wallet information for wallet management
  getMyWallets: () => api.get("/api/custodian/my-wallets"),

  // Create wallet for custodian
  createCustodianWallet: (walletData) =>
    api.post("/api/custodian/my-wallets", walletData),

  // Get sub-investor payment details for a specific payout
  getSubInvestorPayments: (payoutId) =>
    api.get(`/api/custodian/payouts/${payoutId}/sub-investor-payments`),

  // Get sub-investor's wallet information
  getSubInvestorWallets: (subInvestorId) =>
    api.get(`/api/custodian/sub-investors/${subInvestorId}/wallets`),

  // Update sub-investor's bank account details
  updateSubInvestorBankAccount: (subInvestorId, walletId, bankAccountData) =>
    api.put(
      `/api/custodian/sub-investors/${subInvestorId}/wallets/${walletId}/bank-account`,
      bankAccountData
    ),

  // Request bank account edit (creates approval request)
  requestBankAccountEdit: (subInvestorId, walletId, bankAccountData) =>
    api.post(
      `/api/custodian/sub-investors/${subInvestorId}/bank-account-edit-request`,
      { walletId, ...bankAccountData }
    ),

  // Approve bank account edit request
  approveBankAccountEdit: (requestId, comments) =>
    api.put(`/api/custodian/approval-requests/${requestId}/approve-bank-account-edit`, { comments }),

  // Reject bank account edit request
  rejectBankAccountEdit: (requestId, rejectionReason, comments) =>
    api.put(`/api/custodian/approval-requests/${requestId}/reject-bank-account-edit`, { rejectionReason, comments }),

  // Request sub-investor edit (creates approval request)
  requestSubInvestorEdit: (subInvestorId, updateData) =>
    api.post(`/api/custodian/sub-investors/${subInvestorId}/edit-request`, updateData),

  // Approve sub-investor edit request
  approveSubInvestorEdit: (requestId, comments) =>
    api.put(`/api/custodian/approval-requests/${requestId}/approve-sub-investor-edit`, { comments }),

  // Reject sub-investor edit request
  rejectSubInvestorEdit: (requestId, rejectionReason, comments) =>
    api.put(`/api/custodian/approval-requests/${requestId}/reject-sub-investor-edit`, { rejectionReason, comments }),

  // Create new sub-investor
  createSubInvestor: (subInvestorData) =>
    api.post("/api/custodian/sub-investors", subInvestorData),

  // Get single sub-investor details
  getSubInvestor: (id) => api.get(`/api/custodian/sub-investors/${id}`),

  // Update sub-investor
  updateSubInvestor: (id, subInvestorData) =>
    api.put(`/api/custodian/sub-investors/${id}`, subInvestorData),

  // Delete sub-investor
  deleteSubInvestor: (id) => api.delete(`/api/custodian/sub-investors/${id}`),

  // Activate sub-investor
  activateSubInvestor: (id) =>
    api.put(`/api/custodian/sub-investors/${id}/activate`),

  // Deactivate sub-investor
  deactivateSubInvestor: (id) =>
    api.put(`/api/custodian/sub-investors/${id}/deactivate`),

  // Reset sub-investor password
  resetSubInvestorPassword: (id) =>
    api.post(`/api/custodian/sub-investors/${id}/reset-password`),

  // Approval system
  approveL2: (id, comments) =>
    api.put(`/api/custodian/sub-investors/${id}/approve-l2`, { comments }),
  rejectSubInvestor: (id, rejectionReason, comments) =>
    api.put(`/api/custodian/sub-investors/${id}/reject`, {
      rejectionReason,
      comments,
    }),
  getPendingApprovals: (params = {}) =>
    api.get("/api/custodian/pending-approvals", { params }),
  getApprovableSubInvestors: (params = {}) =>
    api.get("/api/custodian/approvable-sub-investors", { params }),
  getApprovalHistory: (params = {}) =>
    api.get("/api/custodian/approval-history", { params }),

  // General approval functions for pending requests
  approveRequest: async (request) => {
    // For general approval requests (activate, deactivate, delete)
    if (
      request.actionType === "activate" ||
      request.actionType === "deactivate" ||
      request.actionType === "delete"
    ) {
      const response = await api.put(
        `/api/users/approval-requests/${request._id}/approve`
      );
      return response.data;
    }
    // For sub-investor creation requests, use specific endpoint
    else if (request.actionType === "create_sub_investor") {
      const response = await api.put(
        `/api/custodian/sub-investors/${request._id}/approve-l2`,
        { comments: "Approved from pending approvals" }
      );
      return response.data;
    }
    throw new Error(`Unsupported action type: ${request.actionType}`);
  },

  rejectRequest: async (request, reason = "") => {
    // For general approval requests (activate, deactivate, delete)
    if (
      request.actionType === "activate" ||
      request.actionType === "deactivate" ||
      request.actionType === "delete"
    ) {
      const response = await api.put(
        `/api/users/approval-requests/${request._id}/reject`,
        { reason }
      );
      return response.data;
    }
    // For sub-investor creation requests, use specific endpoint
    else if (request.actionType === "create_sub_investor") {
      const response = await api.put(
        `/api/custodian/sub-investors/${request._id}/reject`,
        { rejectionReason: reason, comments: reason }
      );
      return response.data;
    }
    throw new Error(`Unsupported action type: ${request.actionType}`);
  },

  // Batch upload sub-investors
  batchUpload: (csvData) =>
    api.post("/api/custodian/sub-investors/batch", { csvData }),

  // Get wallets for sub-investors created by this custodian
  getWallets: (params = {}) => api.get("/api/custodian/wallets", { params }),

  // Get sub-investors without wallets
  getSubInvestorsWithoutWallets: () =>
    api.get("/api/custodian/sub-investors-without-wallets"),

  // Create wallet for sub-investor
  createSubInvestorWallet: (walletData) =>
    api.post("/api/custodian/wallets/create", walletData),

  // Get wallet details
  getWallet: (walletId) => api.get(`/api/custodian/wallets/${walletId}`),

  // Update wallet
  updateWallet: (walletId, walletData) =>
    api.put(`/api/custodian/wallets/${walletId}`, walletData),

  // Get client's bond portfolio (token holdings)
  getClientPortfolio: (clientId) =>
    api.get(`/api/custodian/sub-investors/${clientId}/portfolio`),

  // Get client's allocated bonds from custodian allocations
  getClientAllocatedBonds: (clientId) =>
    api.get(`/api/custodian/sub-investors/${clientId}/allocated-bonds`),

  // Get client's bond allocations from custodian allocations
  getClientAllocations: (clientId, params = {}) =>
    api.get("/api/custodian/allocations", {
      params: { ...params, includeAllocations: true, view: "preview" },
    }),

  // Get all bonds allocated to this custodian
  getAllCustodianAllocations: (params = {}) =>
    api.get("/api/custodian/allocations", {
      params: { ...params, includeAllocations: true, view: "preview" },
    }),

  // Get sub-investors who own a specific bond
  getBondOwners: (bondId, params = {}) =>
    api.get(`/api/custodian/bonds/${bondId}/owners`, { params }),

  // Validate investor IDs
  validateInvestorIds: (investorIds) =>
    api.post("/api/custodian/validate-investor-ids", { investorIds }),

  // Validate investor IDs and wallet addresses for CSV upload
  validateInvestorWallet: (csvData, bondId) =>
    api.post("/api/custodian/validate-investor-wallet", { csvData, bondId }),

  // Get available counterparties for secondary transfers (direct investors and other custodians)
  getCounterparties: (params = {}) =>
    api.get("/api/custodian/counterparties", { params }),

  // ===== CUSTODIAN PAYMENT METHODS =====

  // Get all payment schedules for the current custodian
  getPayments: (params = {}) => api.get("/api/custodian/payments", { params }),

  // Get upcoming payments for the current custodian
  getUpcomingPayments: () => api.get("/api/custodian/payments/upcoming"),

  // Get specific custodian payment details
  getPayment: (paymentId) => api.get(`/api/custodian/payments/${paymentId}`),

  // Upload sub-investor payment distribution CSV
  uploadSubInvestorPayments: (paymentId, data) =>
    api.post(`/api/custodian/payments/${paymentId}/upload`, data),
  approvePaymentL2: (paymentId, data) =>
    api.put(`/api/custodian/payments/${paymentId}/approve-l2`, data),
  rejectPayment: (paymentId, data) =>
    api.put(`/api/custodian/payments/${paymentId}/reject`, data),
  getPendingPaymentApprovals: (params = {}) =>
    api.get("/api/custodian/payments/pending-approvals", { params }),

  // Get payment statistics for the current custodian
  getPaymentStats: () => api.get("/api/custodian/payments/stats/overview"),

  // Debug endpoint to get all custodian payments (for troubleshooting)
  debugGetAllPayments: (params = {}) =>
    api.get("/api/custodian/payments/debug/all", { params }),

  // Download CSV template for batch sub-investor creation
  downloadTemplate: () =>
    api.get("/api/custodian/sub-investors/template", {
      responseType: "blob",
    }),

  // Get bonds by user allocations
  getBondsByUserAllocations: (userId, options = {}) => {
    const params = new URLSearchParams();
    if (options.excludeMatured) {
      params.append('excludeMatured', 'true');
    }
    const queryString = params.toString();
    const url = `/api/allocations/user/${userId}/bonds${queryString ? `?${queryString}` : ''}`;
    return api.get(url).then((response) => response.data);
  },

  // --- Custodian Allocation Two-Layer Approval Workflow ---
  getCustodianAllocationApproval: (bondId) =>
    api.get(`/api/custodian/bonds/${bondId}/allocation-approvals`),
  // Batch fetch allocation approvals for multiple bonds
  getCustodianAllocationApprovals: (bondIds = []) => {
    const param = Array.isArray(bondIds) ? bondIds.join(",") : bondIds;
    return api.get("/api/custodian/allocation-approvals", {
      params: { bondIds: param },
    });
  },
  stageCustodianAllocation: (bondId, allocations) =>
    api.post(`/api/custodian/bonds/${bondId}/allocation-approvals/stage`, {
      allocations,
    }),
  approveCustodianAllocation: (bondId, level) =>
    api.post(`/api/custodian/bonds/${bondId}/allocation-approvals`, { level }),
  // Two-layer finalize approvals (custodian AllocationPreview)
  approveCustodianFinalize: (bondId, level) =>
    api.post(`/api/custodian/bonds/${bondId}/finalize-approvals`, { level }),
  // Batch fetch finalize approvals for multiple bonds
  getCustodianFinalizeApprovals: (bondIds = []) => {
    const param = Array.isArray(bondIds) ? bondIds.join(",") : bondIds;
    return api.get("/api/custodian/finalize-approvals", {
      params: { bondIds: param },
    });
  },

  // Daily Position Report (real-time)
  getDailyPositionReport: () => api.get('/api/custodian/reports/daily-position'),

  // Generate AI summary for report
  generateSummary: (reportData) => api.post('/api/custodian/generate-summary', { reportData }),

  // Bond Holdings aggregated endpoint (replaces separate bonds + allocations queries)
  getBondHoldings: () => api.get('/api/custodian/bond-holdings'),

  // Get holdings as custodian from holdings table
  getHoldingsAsCustodian: (params = {}) => api.get('/api/holdings/as-custodian', { params }),

  // Custodian holding distributions (holdings-derived allocations)
  getHoldingDistributionBonds: (params) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page);
    if (params?.limit) queryParams.append('limit', params.limit);
    if (params?.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString();
    return api.get(`/api/custodian/holding-distributions/bonds${queryString ? `?${queryString}` : ''}`);
  },
  getHoldingDistribution: (bondId) => api.get(`/api/custodian/holding-distributions/bonds/${bondId}`),
  uploadHoldingDistribution: (bondId, formData) => api.post(
    `/api/custodian/holding-distributions/bonds/${bondId}/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  validateHoldingDistribution: (bondId, data) => api.post(
    `/api/custodian/holding-distributions/bonds/${bondId}/validate`,
    data
  ),
  approveHoldingDistribution: (bondId, level) =>
    api.post(`/api/custodian/holding-distributions/bonds/${bondId}/approve`, { level }),

  // Approve manual allocation (L1 or L2)
  approveManualAllocation: (bondId, level) =>
    api.post(`/api/custodian/holding-distributions/bonds/${bondId}/approve`, { level }),

  // Create manual allocation for individual sub-investor
  createManualAllocation: (bondId, data) =>
    api.post(`/api/custodian/holding-distributions/bonds/${bondId}/allocate-individual`, data),

  // Bulk allocate to multiple sub-investors
  bulkAllocate: (bondId, data) =>
    api.post(`/api/custodian/holding-distributions/bonds/${bondId}/allocate-bulk`, data),
};

export const psAPI = {
  // PS holding distributions (holdings-derived allocations)
  getHoldingDistributionBonds: (params) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page);
    if (params?.limit) queryParams.append('limit', params.limit);
    if (params?.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString();
    return api.get(`/api/ps/holding-distributions/bonds${queryString ? `?${queryString}` : ''}`);
  },
  getHoldingDistribution: (bondId) => api.get(`/api/ps/holding-distributions/bonds/${bondId}`),
  uploadHoldingDistribution: (bondId, formData) => api.post(
    `/api/ps/holding-distributions/bonds/${bondId}/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  validateHoldingDistribution: (bondId, data) => api.post(
    `/api/ps/holding-distributions/bonds/${bondId}/validate`,
    data
  ),
  approveHoldingDistribution: (bondId, level) =>
    api.post(`/api/ps/holding-distributions/bonds/${bondId}/approve`, { level }),

  // Create manual allocation for individual investor (custodian or direct investor)
  createManualAllocation: (bondId, data) =>
    api.post(`/api/ps/holding-distributions/bonds/${bondId}/allocate-individual`, data),

  // Create bond token request for individual investor (they will need to approve it)
  createBondTokenRequest: (bondId, data) =>
    api.post(`/api/ps/holding-distributions/bonds/${bondId}/create-bond-token-request`, data),

  // Get bond token requests for a specific bond (created by this PS)
  getBondTokenRequests: (bondId) =>
    api.get(`/api/ps/holding-distributions/bonds/${bondId}/bond-token-requests`),
};

export const custodianCouponPaymentsAPI = {
  getPayments: (params = {}) =>
    api.get('/api/custodian/coupon-payments', { params }),
  getPayment: (paymentId) =>
    api.get(`/api/custodian/coupon-payments/${paymentId}`),
  getPendingApprovals: (params = {}) =>
    api.get('/api/custodian/coupon-payments/pending-approvals', { params }),
  uploadSubInvestorPayments: (paymentId, data) =>
    api.post(`/api/custodian/coupon-payments/${paymentId}/upload`, data),
  autoDistributePreview: (paymentId) =>
    api.get(`/api/custodian/coupon-payments/${paymentId}/auto-distribute-preview`),
  autoDistributeSubInvestorPayments: (paymentId, data) =>
    api.post(`/api/custodian/coupon-payments/${paymentId}/auto-distribute`, data),
  approvePaymentL2: (paymentId, data = {}) =>
    api.put(`/api/custodian/coupon-payments/${paymentId}/approve-l2`, data),
  rejectPayment: (paymentId, data) =>
    api.put(`/api/custodian/coupon-payments/${paymentId}/reject`, data),
};

export const couponDistributionRequestsAPI = {
  createRequests: (formData) =>
    api.post('/api/coupon-distribution-requests', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  getMyRequests: (params = {}) =>
    api.get('/api/coupon-distribution-requests/my', { params }),
  approveRequestL1: (requestId, data = {}) =>
    api.put(`/api/coupon-distribution-requests/${requestId}/approve-l1`, data),
  approveRequestL2: (requestId, data = {}) =>
    api.put(`/api/coupon-distribution-requests/${requestId}/approve-l2`, data),
  rejectRequest: (requestId, data = {}) =>
    api.put(`/api/coupon-distribution-requests/${requestId}/reject`, data)
};

export const maturityDistributionRequestsAPI = {
  createRequests: (formData) =>
    api.post('/api/maturity-distribution-requests', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  getMyRequests: (params = {}) =>
    api.get('/api/maturity-distribution-requests/my-requests', { params }),
  approveRequestL1: (requestId, data = {}) =>
    api.put(`/api/maturity-distribution-requests/${requestId}/approve-l1`, data),
  approveRequestL2: (requestId, data = {}) =>
    api.put(`/api/maturity-distribution-requests/${requestId}/approve-l2`, data),
  rejectRequest: (requestId, data = {}) =>
    api.put(`/api/maturity-distribution-requests/${requestId}/reject`, data)
};

export const maturityPayoutsAPI = {
  getPayouts: (params = {}) =>
    api.get('/api/maturity-payouts', { params }),
  getPayoutStats: () =>
    api.get('/api/maturity-payouts/stats'),
  getPayoutById: (payoutId) =>
    api.get(`/api/maturity-payouts/${payoutId}`)
};

export const custodianMaturityPaymentsAPI = {
  getPayments: (params = {}) =>
    api.get('/api/custodian/maturity-payments', { params }),
  getPayment: (paymentId) =>
    api.get(`/api/custodian/maturity-payments/${paymentId}`),
  getPendingApprovals: (params = {}) =>
    api.get('/api/custodian/maturity-payments/pending-approvals', { params }),
  uploadSubInvestorPayments: (paymentId, data) =>
    api.post(`/api/custodian/maturity-payments/${paymentId}/upload`, data),
  approvePaymentL1: (paymentId, data = {}) =>
    api.put(`/api/custodian/maturity-payments/${paymentId}/approve-l1`, data),
  approvePaymentL2: (paymentId, data = {}) =>
    api.put(`/api/custodian/maturity-payments/${paymentId}/approve-l2`, data),
  approveSubInvestorL1: (paymentId, investorId, data = {}) =>
    api.put(`/api/custodian/maturity-payments/${paymentId}/sub-investors/${investorId}/approve-l1`, data),
  approveSubInvestorL2: (paymentId, investorId, data = {}) =>
    api.put(`/api/custodian/maturity-payments/${paymentId}/sub-investors/${investorId}/approve-l2`, data),
  rejectPayment: (paymentId, data) =>
    api.put(`/api/custodian/maturity-payments/${paymentId}/reject`, data),
};

export const subscriptionAPI = {
  // Get all bonds available for subscription management
  getSubscriptionBonds: (params = {}) =>
    api.get("/api/subscriptions/bonds", { params }),

  getAvailableBondsForInvestors: (params = {}) =>
    api.get("/api/subscriptions/available-bonds", { params }),

  // Add a bond to subscription management
  addSubscriptionBond: (bondData) =>
    api.post("/api/subscriptions/bonds", bondData),

  // Set subscription period for a bond
  setSubscriptionPeriod: (bondId, periodData) =>
    api.post(`/api/subscriptions/bonds/${bondId}/period`, periodData),
  getSubscriptionPeriodApproval: (bondId) =>
    api.get(`/api/subscriptions/bonds/${bondId}/period-approvals`),
  getSubscriptionPeriodApprovals: (bondIds = []) => {
    const param = Array.isArray(bondIds) ? bondIds.join(",") : bondIds;
    return api.get(`/api/subscriptions/period-approvals`, {
      params: { bondIds: param },
    });
  },
  approveSubscriptionPeriod: (bondId, body) =>
    api.post(`/api/subscriptions/bonds/${bondId}/period-approvals`, body),
  getAllocationApproval: (bondId) =>
    api.get(`/api/subscriptions/bonds/${bondId}/allocation-approvals`),
  // Batch fetch allocation approvals for multiple bonds
  getAllocationApprovals: (bondIds = []) => {
    const param = Array.isArray(bondIds) ? bondIds.join(",") : bondIds;
    return api.get("/api/subscriptions/allocation-approvals", {
      params: { bondIds: param },
    });
  },
  stageAllocationUpload: (bondId, formData) =>
    api.post(
      `/api/subscriptions/bonds/${bondId}/allocation-approvals/stage`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    ),
  approveAllocationUpload: (bondId, body) =>
    api.post(`/api/subscriptions/bonds/${bondId}/allocation-approvals`, body),
  // Two-layer finalize approvals (AllocationPreview)
  approveFinalizeAllocation: (bondId, body) =>
    api.post(`/api/subscriptions/bonds/${bondId}/finalize-approvals`, body),

  // Update bond subscription status
  updateSubscriptionStatus: (bondId, status) =>
    api.put(`/api/subscriptions/bonds/${bondId}/status`, { status }),

  // Get bonds ready for allocation (use view='upload' for upload page, view='preview' for preview page)
  getAllocationBonds: (params = {}) =>
    api.get("/api/subscriptions/allocations", { params }),

  // Get all bonds with their allocation data in a single optimized call
  getBondsWithAllocations: (params = {}) =>
    api.get("/api/allocations/bonds-with-allocations", { params }),

  // Get all bonds with Primary Subscriber allocations
  getPSBondsWithAllocations: (params = {}) =>
    api.get("/api/allocations/ps-bonds-with-allocations", { params }),

  // Upload allocation CSV
  uploadAllocationCSV: (bondId, formData) =>
    api.post(`/api/subscriptions/bonds/${bondId}/allocation`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),

  // Get allocation data for a bond
  getBondAllocation: (bondId) =>
    api.get(`/api/subscriptions/bonds/${bondId}/allocation`),

  // Finalize allocation
  finalizeAllocation: (bondId, allocationData) =>
    api.post(`/api/subscriptions/bonds/${bondId}/finalize`, allocationData),

  // Assign bond to custodian
  assignBondToCustodian: (bondId, custodianId, allocationData) =>
    api.post(`/api/subscriptions/bonds/${bondId}/assign-custodian`, {
      custodianId,
      allocationData,
    }),

  // Validate allocation data (investor IDs and wallet addresses)
  validateAllocationData: (allocations) =>
    api.post("/api/subscriptions/validate-allocation", { allocations }),

  // Update payment reference for an investor
  updatePaymentReference: (bondId, paymentData) =>
    api.put(`/api/allocations/${bondId}/payment-reference`, paymentData),

  // Get current investor's bond allocations (Investor only)
  getMyAllocations: (params = {}) =>
    api.get("/api/allocations/my-allocations", { params }),

  // Get current investor's allocations assigned by Primary Subscribers
  getMyPrimarySubscriberAllocations: (params = {}) =>
    api.get("/api/allocations/my-primary-subscriber-allocations", { params }),

  // ===== CUSTODIAN-SPECIFIC METHODS =====

  // Get bonds allocated to custodian by Facility Agent
  getCustodianAllocationBonds: (params = {}) =>
    api.get("/api/custodian/allocations", { params }),

  // Upload custodian allocation CSV
  uploadCustodianAllocationCSV: (bondId, allocationData) =>
    api.post(`/api/custodian/bonds/${bondId}/allocation`, allocationData),

  // Get custodian allocation summary for a bond
  getCustodianBondAllocationSummary: (bondId) =>
    api.get(`/api/custodian/bonds/${bondId}/allocation`),

  // Deprecated: kept temporarily for backward compatibility. Use getCustodianBondAllocationSummary instead.
  getCustodianBondAllocation: (bondId) =>
    api.get(`/api/custodian/bonds/${bondId}/allocation`),

  // Finalize custodian allocation
  finalizeCustodianAllocation: (bondId, allocationData) =>
    api.post(
      `/api/custodian/bonds/${bondId}/allocation/finalize`,
      allocationData
    ),
};

export const allocationRequestsAPI = {
  createRequest: (data) => api.post("/api/allocation-requests", data),
  getMyRequests: () => api.get("/api/allocation-requests/my"),
  deleteRequest: (requestId) => api.delete(`/api/allocation-requests/${requestId}`),
  // Investor internal L1/L2 approval
  approveInvestorL1: (requestId) => api.post(`/api/allocation-requests/${requestId}/investor/approve-l1`),
  approveInvestorL2: (requestId) => api.post(`/api/allocation-requests/${requestId}/investor/approve-l2`),
  getRequestsByBond: (bondId) => api.get(`/api/allocation-requests/bond/${bondId}`),
  getCustodianSubInvestorRequests: () =>
    api.get("/api/allocation-requests/custodian/sub-investors"),
  getPsSubInvestorRequests: () =>
    api.get("/api/allocation-requests/ps/sub-investors"),
  createAmountChangeRequest: (requestId, submissionId, data) =>
    api.post(
      `/api/allocation-requests/${requestId}/submissions/${submissionId}/change-amount`,
      data
    ),
  updateSubmissionAmount: (requestId, submissionId, data) =>
    api.put(
      `/api/allocation-requests/${requestId}/submissions/${submissionId}/amount`,
      data
    ),
  getMyPendingChangeRequests: () =>
    api.get("/api/allocation-requests/change-requests/my"),
  getCustodianPendingChangeRequests: () =>
    api.get("/api/allocation-requests/change-requests/custodian"),
  approveChangeRequest: (changeRequestId) =>
    api.post(`/api/allocation-requests/change-requests/${changeRequestId}/approve`),
  rejectChangeRequest: (changeRequestId) =>
    api.post(`/api/allocation-requests/change-requests/${changeRequestId}/reject`),
  getBatchRequestsByBonds: (bondIds) =>
    api.post("/api/allocation-requests/bonds/batch", { bondIds }),
  // Primary Subscriber endpoints
  getBondsByPrimarySubscriber: () =>
    api.get("/api/allocation-requests/primary-subscriber/bonds"),
  getRequestsByBondAndPS: (bondId) =>
    api.get(`/api/allocation-requests/bond/${bondId}/primary-subscriber`),
  // Custodian endpoints
  getBondsByCustodian: (params) =>
    api.get("/api/allocation-requests/custodian/bonds", { params }),
};

// LOC Approval API (new aggregated approval system)
export const locApprovalAPI = {
  // Get or create LOC approval for an investor
  getOrCreateApproval: (bondId, investorId, custodianId, primarySubscriberId) =>
    api.post("/api/loc-approvals/get-or-create", { bondId, investorId, custodianId, primarySubscriberId }),

  // L1 and L2 approvals
  approveL1: (locApprovalId, notes) =>
    api.post("/api/loc-approvals/approve-l1", { locApprovalId, notes }),
  approveL2: (locApprovalId, notes) =>
    api.post("/api/loc-approvals/approve-l2", { locApprovalId, notes }),

  // Bulk L2 approval (returns job)
  bulkApproveL2: (locApprovalIds, notes) =>
    api.post("/api/loc-approvals/bulk-approve-l2", { locApprovalIds, notes }),

  // Rejection
  reject: (locApprovalId, reason) =>
    api.post("/api/loc-approvals/reject", { locApprovalId, reason }),

  // Update amount
  updateAmount: (locApprovalId, amount) =>
    api.put("/api/loc-approvals/update-amount", { locApprovalId, amount }),

  // Get LOC approvals
  getApprovalsByBond: (bondId) =>
    api.get(`/api/loc-approvals/bond/${bondId}`),
  getMyApprovals: (page = 1, limit = 10, locStatus = null) => {
    const params = { page, limit };
    if (locStatus && locStatus !== 'all') {
      params.locStatus = locStatus;
    }
    return api.get("/api/loc-approvals/my", { params });
  },
  getAllMyApprovals: (locStatus = null) => {
    const params = {};
    if (locStatus && locStatus !== 'all') {
      params.locStatus = locStatus;
    }
    return api.get("/api/loc-approvals/my/all", { params });
  },
  getApprovalById: (locApprovalId) =>
    api.get(`/api/loc-approvals/${locApprovalId}`),
};

// Jobs API
export const jobsAPI = {
  getJobStatus: (jobId) => api.get(`/api/jobs/${jobId}`),
  getMyJobs: (limit) => api.get("/api/jobs/my", { params: { limit } }),
};

export const facilityAgentAPI = {
  // Validate investor IDs and wallet addresses for CSV upload
  validateInvestorWallet: (csvData, bondId) =>
    api.post("/api/facility-agent/validate-investor-wallet", {
      csvData,
      bondId,
    }),

  // Get token holders (investors and custodians, excluding sub-accounts and sub-investors)
  getTokenHolders: (params = {}) =>
    api.get("/api/facility-agent/token-holders", { params }),
};

// Maturity approvals (two-layer) API
export const maturityApprovalsAPI = {
  getState: (bondId) => api.get(`/api/maturity-approvals/${bondId}`),
  // Batch fetch maturity approval states for multiple bonds in one request
  // Backend expected to accept comma separated bondIds: /api/maturity-approvals?bondIds=id1,id2
  // Fallback logic in caller will degrade to per-bond calls if this endpoint is unsupported.
  getStates: (bondIds = []) => {
    if (!Array.isArray(bondIds))
      return api.get("/api/maturity-approvals", { params: { bondIds } });
    const param = bondIds.filter(Boolean).join(",");
    if (!param) return Promise.resolve({ data: { data: {} } });
    return api.get("/api/maturity-approvals", { params: { bondIds: param } });
  },
  approve: (bondId, data) =>
    api.post(`/api/maturity-approvals/${bondId}/approve`, data),
};

export const paymentsAPI = {
  // Get all payments with filtering
  getPayments: (params = {}) => api.get("/api/payments", { params }),

  // Get upcoming payments
  getUpcomingPayments: () => api.get("/api/payments/upcoming"),

  // Get payment by ID
  getPayment: (paymentId) => api.get(`/api/payments/${paymentId}`),

  // Get payments for a specific bond
  getBondPayments: (bondId) => api.get(`/api/payments/bond/${bondId}`),

  // Create new payment
  createPayment: (paymentData) => api.post("/api/payments", paymentData),

  // Update payment status
  updatePaymentStatus: (paymentId, data) =>
    api.put(`/api/payments/${paymentId}/status`, data),

  // Update payment with full data
  updatePayment: (paymentId, data) =>
    api.put(`/api/payments/${paymentId}`, data),

  // Upload payment file
  uploadPaymentFile: (paymentId, data) =>
    api.post(`/api/payments/${paymentId}/upload`, data),

  // Upload payment CSV and update custodian payments
  uploadPaymentCSV: (paymentId, data) =>
    api.post(`/api/payments/${paymentId}/upload-csv`, data),

  submitCouponDistributionDraft: (paymentId, formData) =>
    api.post(`/api/payments/${paymentId}/coupon-distribution-draft`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  previewCouponDistributionDraft: (paymentId, data) =>
    api.post(`/api/payments/${paymentId}/coupon-distribution-preview`, data),

  // Delete payment
  deletePayment: (paymentId) => api.delete(`/api/payments/${paymentId}`),

  // Get payment statistics
  getPaymentStats: () => api.get("/api/payments/stats/overview"),

  // Approval workflow methods
  approveL2PaymentDistribution: (paymentId, data) =>
    api.put(`/api/payments/${paymentId}/approve-l2`, data),
  rejectPaymentDistribution: (paymentId, data) =>
    api.put(`/api/payments/${paymentId}/reject`, data),
  getPendingPaymentApprovals: (params = {}) =>
    api.get("/api/payments/pending-approvals", { params }),

  // Import payment dates
  getBondsWithNullPaymentDates: () =>
    api.get("/api/payments/bonds-with-null-dates"),
  importPaymentDates: (bondId, csvData) =>
    api.post("/api/payments/import-payment-dates", { bondId, csvData }),

  updatePaymentDate: (paymentId, data) =>
    api.put(`/api/payments/${paymentId}/payment-date`, data),
};

export const redemptionsAPI = {
  // Get all redemptions with filtering and pagination
  getAllRedemptions: (params = {}) => api.get("/api/redemptions", { params }),

  // Get redemptions pending approval for issuer
  getPendingApprovalForIssuer: () =>
    api.get("/api/redemptions/pending-approval/issuer"),

  // Get single redemption by ID
  getRedemption: (redemptionId) => api.get(`/api/redemptions/${redemptionId}`),

  // Get redemptions by bond ID
  getRedemptionsByBond: (bondId, params = {}) =>
    api.get(`/api/redemptions/bond/${bondId}`, { params }),

  // Get redemption status by bond ID
  getRedemptionStatusByBond: (bondId) =>
    api.get(`/api/redemptions/status/${bondId}`),

  // Prepare redemptions for a bond (FA action)
  prepareRedemptions: (bondId, data) =>
    api.post(`/api/redemptions/prepare/${bondId}`, data),

  // Submit redemptions for approval (FA action)
  submitForApproval: (bondId) =>
    api.post(`/api/redemptions/submit-approval/${bondId}`),

  // Approve redemptions (Issuer action)
  approveRedemptions: (data) => api.post("/api/redemptions/approve", data),

  // Reject redemptions (Issuer action)
  rejectRedemptions: (data) => api.post("/api/redemptions/reject", data),

  // Process redemptions (System/Admin action)
  processRedemptions: (data) => api.post("/api/redemptions/process", data),

  // Complete redemptions (System/Admin action)
  completeRedemptions: (data) => api.post("/api/redemptions/complete", data),

  // Burn tokens for a bond (FA action)
  burnTokens: (bondId, data) =>
    api.post(`/api/redemptions/burn-tokens/${bondId}`, data),

  // Get upcoming redemptions
  getUpcomingRedemptions: (params = {}) =>
    api.get("/api/redemptions/upcoming/all", { params }),

  // Get redemption statistics
  getRedemptionStatistics: (params = {}) =>
    api.get("/api/redemptions/statistics/summary", { params }),

  // Update redemption notes
  updateRedemptionNotes: (redemptionId, data) =>
    api.patch(`/api/redemptions/${redemptionId}/notes`, data),
};

// Maturity Payment Schedule API (new table for FA maturity redemption payments)
export const maturityPaymentSchedulesAPI = {
  // Get all maturity payment schedules for Facility Agent
  getMaturityPaymentSchedules: (params = {}) =>
    api.get("/api/facility-agent/maturity-payment-schedules", { params }),

  // Get maturity payment schedule by bond ID
  getMaturityPaymentScheduleByBond: (bondId) =>
    api.get(`/api/facility-agent/maturity-payment-schedules/bond/${bondId}`),

  // Get maturity payment schedule by ID
  getMaturityPaymentSchedule: (scheduleId) =>
    api.get(`/api/facility-agent/maturity-payment-schedules/${scheduleId}`),

  // Upload CSV for maturity payment investors
  uploadMaturityPaymentCSV: (scheduleId, formData) =>
    api.post(`/api/facility-agent/maturity-payment-schedules/${scheduleId}/upload-csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Get investors for a maturity payment schedule
  getMaturityPaymentInvestors: (scheduleId) =>
    api.get(`/api/facility-agent/maturity-payment-schedules/${scheduleId}/investors`),

  // Submit payment reference (proof of settlement or RENTAS reference)
  submitPaymentReference: (scheduleId, investorId, formData) =>
    api.post(`/api/facility-agent/maturity-payment-schedules/${scheduleId}/investors/${investorId}/submit-payment-reference`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Approve L2 and create maturity distribution request
  approveL2AndCreateRequest: (scheduleId, investorId) =>
    api.put(`/api/facility-agent/maturity-payment-schedules/${scheduleId}/investors/${investorId}/approve-l2`),

  // Approve maturity payment (individual investor)
  approveMaturityPayment: (scheduleId, investorId, data) =>
    api.put(`/api/facility-agent/maturity-payment-schedules/${scheduleId}/investors/${investorId}/approve`, data),

  // Bulk approve maturity payments
  bulkApproveMaturityPayments: (scheduleId, data) =>
    api.put(`/api/facility-agent/maturity-payment-schedules/${scheduleId}/bulk-approve`, data),

  // Update maturity date
  updateMaturityDate: (scheduleId, maturityDate) =>
    api.put(`/api/facility-agent/maturity-payment-schedules/${scheduleId}/maturity-date`, { maturityDate }),
};

export const redemptionPaymentsAPI = {
  // Get all redemption payments with filtering and pagination
  getRedemptionPayments: (params = {}) =>
    api.get("/api/redemption-payments", { params }),

  // Get redemption payment by ID
  getRedemptionPayment: (id) => api.get(`/api/redemption-payments/${id}`),

  // Create redemption payment
  createRedemptionPayment: (data) =>
    api.post("/api/redemption-payments/create", data),

  // Update redemption payment status
  updatePaymentStatus: (id, data) =>
    api.put(`/api/redemption-payments/${id}/status`, data),

  // Get redemption payments by facility agent
  getRedemptionPaymentsByFacilityAgent: (params = {}) =>
    api.get("/api/redemption-payments/facility-agent/me", { params }),

  // Get redemption payments by bond
  getRedemptionPaymentsByBond: (bondId) =>
    api.get(`/api/redemption-payments/bond/${bondId}`),

  // Delete redemption payment
  deleteRedemptionPayment: (id) => api.delete(`/api/redemption-payments/${id}`),

  // Upload redemption CSV and process payment
  uploadRedemptionCSV: (redemptionId, data) =>
    api.post(`/api/redemption-payments/${redemptionId}/upload-csv`, data),

  // Get pending L2 approvals
  getPendingL2Approvals: (params = {}) =>
    api.get("/api/redemption-payments/pending-approvals", { params }),

  // Approve L2 payment
  approveL2Payment: (paymentId, data) =>
    api.put(`/api/redemption-payments/${paymentId}/approve-l2`, data),

  // Reject payment
  rejectPayment: (paymentId, data) =>
    api.put(`/api/redemption-payments/${paymentId}/reject`, data),
};

export const whitelistsAPI = {
  // List whitelist entries (new module) - status filter optional
  getWhitelistRequests: (params = {}) =>
    api.get("/api/whitelist/entries", { params }),

  // Get single whitelist entry
  getWhitelistRequest: (id) => api.get(`/api/whitelist/entries/${id}`),

  // Record Level 1 approval (metadata only)
  level1Approve: (id, data = {}) =>
    api.post(`/api/whitelist/entries/${id}/level1-approve`, data),

  // Final (L2) approval - triggers blockchain whitelist update
  approveRequest: (id, notes) =>
    api.post(`/api/whitelist/entries/${id}/approve`, { notes }),

  // Reject whitelist entry
  rejectRequest: (id, reason, notes) =>
    api.post(`/api/whitelist/entries/${id}/reject`, { reason, notes }),

  // Stats endpoint
  getStats: (bondId) =>
    api.get("/api/whitelist/stats", { params: bondId ? { bondId } : {} }),

  // Bulk L2 approve
  bulkApprove: (ids, notes) =>
    api.post("/api/whitelist/entries/bulk-approve", { ids, notes }),

  // Bulk reject
  bulkReject: (ids, reason, notes) =>
    api.post("/api/whitelist/entries/bulk-reject", { ids, reason, notes }),

  // Create whitelist entries from allocation data
  createFromAllocation: (allocationData) =>
    api.post("/api/whitelist/entries/create-from-allocation", allocationData),
};

export const secondaryTransfersAPI = {
  // Create a new secondary transfer request
  createTransfer: (transferData) =>
    api.post("/api/secondary-transfers/create", transferData),

  // Get all transfers with filtering and pagination
  getTransfers: (params = {}) =>
    api.get("/api/secondary-transfers", { params }),

  // Get ALL transfers (administrative view - bypasses role restrictions)
  getAllTransfers: (params = {}) =>
    api.get("/api/secondary-transfers/all", { params }),

  // Get my transfers (for current user)
  getMyTransfers: (params = {}) =>
    api.get("/api/secondary-transfers/my-transfers", { params }),

  // Get transfer by ID
  getTransfer: (transferId) =>
    api.get(`/api/secondary-transfers/${transferId}`),

  // Get transfer by request ID
  getTransferByRequestId: (transferRequestId) =>
    api.get(`/api/secondary-transfers/request/${transferRequestId}`),

  // Get transfers by bond ID
  getTransfersByBond: (bondId, params = {}) =>
    api.get(`/api/secondary-transfers/bond/${bondId}`, { params }),

  // Update transfer status
  updateTransferStatus: (transferId, statusData) =>
    api.put(`/api/secondary-transfers/${transferId}/status`, statusData),

  // Confirm transfer
  confirmTransfer: (transferId, confirmationData) =>
    api.put(`/api/secondary-transfers/${transferId}/confirm`, confirmationData),

  // Reject transfer
  rejectTransfer: (transferId, rejectionData) =>
    api.put(`/api/secondary-transfers/${transferId}/reject`, rejectionData),

  // Cancel transfer
  cancelTransfer: (transferId, cancellationData) =>
    api.put(`/api/secondary-transfers/${transferId}/cancel`, cancellationData),

  // Complete settlement
  completeSettlement: (transferId, settlementData) =>
    api.put(
      `/api/secondary-transfers/${transferId}/complete-settlement`,
      settlementData
    ),

  // Validate counterparty
  validateCounterparty: (transferId, validationData) =>
    api.put(
      `/api/secondary-transfers/${transferId}/validate-counterparty`,
      validationData
    ),

  // L1 / L2 approve transfer (dual control)
  approveTransfer: (transferId, approvalData = {}) =>
    api.put(`/api/secondary-transfers/${transferId}/approve`, approvalData),

  // Get transfer statistics
  getStatistics: (params = {}) =>
    api.get("/api/secondary-transfers/statistics", { params }),
};

export const payoutsAPI = {
  // Get payouts for the logged-in investor
  getPayouts: (params = {}) => api.get("/api/payouts", { params }),

  // Get payout by ID
  getPayout: (payoutId) => api.get(`/api/payouts/${payoutId}`),

  // Get payout statistics
  getPayoutStats: () => api.get("/api/payouts/stats"),

  // Get payouts by date range
  getPayoutsByDateRange: (startDate, endDate) =>
    api.get("/api/payouts/date-range", {
      params: { startDate, endDate },
    }),

  // Admin: Get payouts for any investor
  getPayoutsByInvestor: (investorId, params = {}) =>
    api.get(`/api/payouts/admin/investor/${investorId}`, { params }),

  // Admin: Get payout statistics for any investor
  getPayoutStatsByInvestor: (investorId) =>
    api.get(`/api/payouts/admin/investor/${investorId}/stats`),

  // Admin: Create payout
  createPayout: (payoutData) => api.post("/api/payouts/admin", payoutData),

  // Admin: Update payout
  updatePayout: (payoutId, payoutData) =>
    api.put(`/api/payouts/admin/${payoutId}`, payoutData),

  // Admin: Delete payout
  deletePayout: (payoutId) => api.delete(`/api/payouts/admin/${payoutId}`),
};

export const tokenTransfersAPI = {
  // Get all token transfers with filtering and pagination
  getTokenTransfers: (params = {}) =>
    api.get("/api/token-transfers", { params }),

  // Get token transfer by ID
  getTokenTransfer: (id) => api.get(`/api/token-transfers/${id}`),

  // Create new token transfer
  createTokenTransfer: (transferData) =>
    api.post("/api/token-transfers", transferData),

  // Update token transfer
  updateTokenTransfer: (id, transferData) =>
    api.put(`/api/token-transfers/${id}`, transferData),

  // Update token transfer status
  updateTokenTransferStatus: (id, statusData) =>
    api.put(`/api/token-transfers/${id}/status`, statusData),

  // Approve token transfer
  approveTokenTransfer: (id, approvalData) =>
    api.post(`/api/token-transfers/${id}/approve`, approvalData),

  // Reject token transfer
  rejectTokenTransfer: (id, rejectionData) =>
    api.post(`/api/token-transfers/${id}/reject`, rejectionData),

  // Start processing token transfer
  startProcessing: (id, processingData) =>
    api.post(`/api/token-transfers/${id}/process`, processingData),

  // Complete token transfer
  completeTokenTransfer: (id, completionData) =>
    api.post(`/api/token-transfers/${id}/complete`, completionData),

  // Mark token transfer as failed
  failTokenTransfer: (id, failureData) =>
    api.post(`/api/token-transfers/${id}/fail`, failureData),

  // Cancel token transfer
  cancelTokenTransfer: (id, cancellationData) =>
    api.post(`/api/token-transfers/${id}/cancel`, cancellationData),

  // Retry failed token transfer
  retryTokenTransfer: (id, retryData) =>
    api.post(`/api/token-transfers/${id}/retry`, retryData),

  // Update blockchain transaction details
  updateBlockchainTransaction: (id, transactionData) =>
    api.put(
      `/api/token-transfers/${id}/blockchain-transaction`,
      transactionData
    ),

  // Get token transfers by user
  getTokenTransfersByUser: (userId, params = {}) =>
    api.get(`/api/token-transfers/user/${userId}`, { params }),

  // Get token transfers by bond
  getTokenTransfersByBond: (bondId, params = {}) =>
    api.get(`/api/token-transfers/bond/${bondId}`, { params }),

  // Get pending token transfers
  getPendingTokenTransfers: (params = {}) =>
    api.get("/api/token-transfers/pending", { params }),

  // Get retryable token transfers
  getRetryableTokenTransfers: (params = {}) =>
    api.get("/api/token-transfers/retryable", { params }),

  // Get token transfer statistics
  getTokenTransferStats: (params = {}) =>
    api.get("/api/token-transfers/stats", { params }),

  // Batch approve token transfers
  batchApproveTokenTransfers: (transferIds, approvalData) =>
    api.post("/api/token-transfers/batch-approve", {
      transferIds,
      ...approvalData,
    }),

  // Get transfer summary for reporting
  getTransferSummary: (params = {}) =>
    api.get("/api/token-transfers/summary", { params }),

  // New: unified parser that normalizes various response shapes
  _parseListResponse: (resp) => {
    const root = resp?.data?.data || resp?.data || resp;
    const arr = Array.isArray(root?.data)
      ? root.data
      : Array.isArray(root?.tokenTransfers)
        ? root.tokenTransfers
        : Array.isArray(root?.transfers)
          ? root.transfers
          : Array.isArray(root)
            ? root
            : [];
    const pagination = root?.pagination || null;
    const cursor = pagination?.cursor || null;
    return { list: arr, pagination, cursor };
  },

  // New: cursor-based fetch (after / before) leveraging backend enhancements
  getTokenTransfersCursor: async (params = {}) => {
    const finalParams = { limit: 100, ...params, limit: params.limit ?? 100 };
    const resp = await api.get("/api/token-transfers", { params: finalParams });
    const { list, pagination, cursor } =
      tokenTransfersAPI._parseListResponse(resp);
    return {
      transfers: list,
      pagination,
      cursor,
      hasNextPage: !!pagination?.hasNextPage,
      hasPrevPage: !!pagination?.hasPrevPage,
      endCursor: cursor?.end,
      startCursor: cursor?.start,
    };
  },

  // Create redemption token transfers for all token holders using web3 API
  createRedemptionTransfersFromWeb3Holders: (data) =>
    api.post("/api/token-transfers/redemption/from-web3-holders", data),
};

// Utility functions - using centralized money utils
export const formatCurrency = (amount, currency = "MYR") => {
  return formatCurrencyUtil(amount, currency);
};

export const formatNumber = (number) => {
  return new Intl.NumberFormat("en-MY").format(number);
};

export const formatDate = (date) => {
  return new Date(date).toLocaleDateString("en-MY", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatDateTime = (date) => {
  return new Date(date).toLocaleString("en-MY", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Format currency with compact notation for large numbers
export const formatCompactCurrency = (amount, currency = "MYR") => {
  return formatCompactCurrencyUtil(amount, currency);
};

// Export default api instance
export const locTemplatesAPI = {
  // Create a new LOC template
  createTemplate: (data) => api.post("/api/loc-templates", data),

  // Get template by ID
  getTemplate: (templateId) => api.get(`/api/loc-templates/${templateId}`),

  // Get templates for a specific bond (for the authenticated PS)
  getTemplatesByBond: (bondId) => api.get(`/api/loc-templates/bond/${bondId}`),

  // Get template for a specific bond and primary subscriber (for investors)
  getTemplateByBondAndPS: (bondId, primarySubscriberId) =>
    api.get(`/api/loc-templates/bond/${bondId}/ps/${primarySubscriberId}`),

  // Get available primary subscribers with templates for a bond (for investors)
  getPrimarySubscribersWithTemplates: (bondId) =>
    api.get(`/api/loc-templates/bond/${bondId}/primary-subscribers`),

  // Get all templates for the authenticated primary subscriber
  getMyTemplates: () => api.get("/api/loc-templates/my-templates"),

  // Update template
  updateTemplate: (templateId, data) =>
    api.put(`/api/loc-templates/${templateId}`, data),

  // Delete template
  deleteTemplate: (templateId) =>
    api.delete(`/api/loc-templates/${templateId}`),

  // Generate PDF from template and field data
  generatePDF: (templateId, fieldData) =>
    api.post("/api/loc-templates/generate-pdf", { templateId, fieldData }, {
      responseType: 'blob' // Important for PDF download
    }),

  // Preview PDF from template data without saving (for template creation)
  previewPDF: (templateData, fieldData, bondId, primarySubscriberId) =>
    api.post("/api/loc-templates/preview-pdf", { templateData, fieldData, bondId, primarySubscriberId }, {
      responseType: 'blob' // Important for PDF download
    }),

  // Get PDF form fields from a template file
  getPDFFormFields: (filename = 'Sample-PDF-Fillable.pdf') =>
    api.get(`/api/loc-templates/pdf-fields/${filename}`),

  // Get PDF template file for preview
  getPDFTemplate: (filename = 'Sample-PDF-Fillable.pdf') =>
    api.get(`/api/loc-templates/pdf-template/${filename}`, {
      responseType: 'blob'
    }),
};

export const subAgreementTemplatesAPI = {
  // Create a new Sub Agreement template
  createTemplate: (data) => api.post("/api/sub-agreement-templates", data),

  // Get template by ID
  getTemplate: (templateId) => api.get(`/api/sub-agreement-templates/${templateId}`),

  // Get template for a specific bond (for Primary Subscribers)
  getTemplateByBond: (bondId) => api.get(`/api/sub-agreement-templates/bond/${bondId}`),

  // Get all templates for the authenticated Facility Agent
  getMyTemplates: () => api.get("/api/sub-agreement-templates/my-templates"),

  // Get all bond IDs that have active sub-agreement templates
  getBondsWithActiveTemplates: () => api.get("/api/sub-agreement-templates/bonds-with-templates"),

  // Update template
  updateTemplate: (templateId, data) =>
    api.put(`/api/sub-agreement-templates/${templateId}`, data),

  // Delete template
  deleteTemplate: (templateId) =>
    api.delete(`/api/sub-agreement-templates/${templateId}`),

  // Generate PDF from template and field data
  generatePDF: (templateId, fieldData) =>
    api.post("/api/sub-agreement-templates/generate-pdf", { templateId, fieldData }, {
      responseType: 'blob'
    }),

  // Preview PDF from template data without saving (for template creation)
  previewPDF: (templateData, fieldData) =>
    api.post("/api/sub-agreement-templates/preview-pdf", { templateData, fieldData }, {
      responseType: 'blob'
    }),

  // Get PDF form fields from a template file
  getPDFFormFields: (filename = 'Sub_Agreement_Sample.pdf') =>
    api.get(`/api/sub-agreement-templates/pdf-fields/${filename}`),

  // Get PDF template file for preview
  getPDFTemplate: (filename = 'Sub_Agreement_Sample.pdf') =>
    api.get(`/api/sub-agreement-templates/pdf-template/${filename}`, {
      responseType: 'blob'
    }),
};

export const subAgreementRequestsAPI = {
  createRequest: (data) => api.post("/api/sub-agreement-requests", data),
  getMyRequests: () => api.get("/api/sub-agreement-requests/my"),
  getRequestById: (requestId) => api.get(`/api/sub-agreement-requests/${requestId}`),
  getRequestsByBondForFA: (bondId) => api.get(`/api/sub-agreement-requests/bond/${bondId}`),
  getBondsWithRequestsForFA: (page = 1, limit = 10) =>
    api.get("/api/sub-agreement-requests/bonds/with-requests", { params: { page, limit } }),
  getPendingApprovalCount: () => api.get("/api/sub-agreement-requests/pending-approval-count"),
  updateSubmissionAmount: (requestId, submissionId, data) =>
    api.put(`/api/sub-agreement-requests/${requestId}/submissions/${submissionId}/amount`, data),
  updateTotalRequestedAmount: (requestId, data) =>
    api.put(`/api/sub-agreement-requests/${requestId}/total-amount`, data),
  // Approval methods
  approveL1: (requestId, data) =>
    api.post(`/api/sub-agreement-requests/${requestId}/approve-l1`, data),
  approveL2: (requestId, data) =>
    api.post(`/api/sub-agreement-requests/${requestId}/approve-l2`, data),
  rejectRequest: (requestId, data) =>
    api.post(`/api/sub-agreement-requests/${requestId}/reject`, data),
  bulkApproveL1: (bondId, data) =>
    api.post(`/api/sub-agreement-requests/bond/${bondId}/bulk-approve-l1`, data),
  bulkApproveL2: (bondId, data) =>
    api.post(`/api/sub-agreement-requests/bond/${bondId}/bulk-approve-l2`, data),
  bulkReject: (bondId, data) =>
    api.post(`/api/sub-agreement-requests/bond/${bondId}/bulk-reject`, data),
  // Delete methods (Primary Subscriber only)
  deleteSubmission: (requestId, submissionId) =>
    api.delete(`/api/sub-agreement-requests/${requestId}/submissions/${submissionId}`),
  deleteRequest: (requestId) =>
    api.delete(`/api/sub-agreement-requests/${requestId}`),
  // Generate Sub Agreement PDF
  generatePDF: (data) =>
    api.post("/api/sub-agreement-requests/generate-pdf", data, {
      responseType: 'blob'
    }),
};

// Primary Subscriber Bond Token Allocation API
export const primarySubscriberAllocationAPI = {
  // Get bonds with PS holdings from holdings table
  getBondTokenAllocations: () =>
    api.get('/api/facility-agent/primary-subscriber/bond-token-allocations'),
};

export default api;
